import { create } from 'zustand';
import { fetchPlanningTasks, fetchPlanningPreferences, savePlanningPreferences, createPlanningTask, scheduleFase, deletePlanningTask, updatePlanningTask, type EasykitConfig } from '@/api/planning';
import { fetchInkoopStatusBulk, type InkoopStatus } from '@/api/onderaannemers';
import { getBoardRange } from '@/utils/planningWeeks';
import { loadZoom, saveZoom, stepZoom } from '@/utils/planningZoom';
import type { OdooTask } from '@/types/odoo';

/** `per_day`: the read-only day overview (no installer rows, no editing). */
export type ViewFilter = 'installers' | 'pl_inst' | 'per_day';

interface CreateMode {
  type: 'feestdag' | 'verlof' | 'vrije_taak';
  name?: string;
  /** vrije_taak only: «Dit is een interventie». */
  interventie?: boolean;
}

interface ScheduleProjectMode {
  projectId: number;
  projectName: string;
  faseId: number;
  faseName: string;
  address?: string;
}

interface PlanningState {
  tasks: OdooTask[];
  userMap: Map<number, string>;
  projectleiderMap: Map<number, string>;
  employeeMap: Map<number, boolean>;
  categoryMap: Map<number, string>;
  projectNameMap: Map<number, string>;
  werfMap: Map<number, string>;
  soNameMap: Map<number, string>;
  vorderingProgress: Map<number, { percentage: number; status: string }>;
  eindeWerkenProjects: Set<number>;
  gevorderdFases: Set<number>;
  inkoopStatusMap: Map<number, InkoopStatus>;
  showInkoopStatus: boolean;
  loading: boolean;
  error: string | null;
  clearError: () => void;
  /** A message that asks the user to act: stays until closed (never auto-dismissed). */
  warning: string | null;
  clearWarning: () => void;
  lastRefreshed: Date | null;
  weekOffset: number;
  viewFilter: ViewFilter;
  collapsedGroups: Set<number>;
  createMode: CreateMode | null;
  scheduleProjectMode: ScheduleProjectMode | null;
  scheduleTargetCell: { dateKey: string; installerId: number } | null;
  showProjectSearch: boolean;
  showAllInstallers: boolean;
  /** "Scroll door weken": several weeks side by side instead of one. */
  multiWeek: boolean;
  /** Board zoom factor (1 = 100%), per device. */
  zoom: number;
  hideCompleted: boolean;
  projectFilter: string;
  /** Deeplink-only: Odoo project_id used as a reliable OR fallback when the
   *  project name from sale.order.project_name differs from project.project.name. */
  projectIdFilter: number | null;
  easykitConfig: EasykitConfig | null;
  installerRowOrder: number[];
  installerRowOrderHydrated: boolean;

  fetchPlanning: (weekOffset?: number, opts?: { silent?: boolean }) => Promise<void>;
  nextWeek: () => void;
  prevWeek: () => void;
  goToCurrentWeek: () => void;
  setWeekOffset: (offset: number) => void;
  setViewFilter: (filter: ViewFilter) => void;
  toggleGroup: (groupId: number) => void;
  enterCreateMode: (type: CreateMode['type'], name?: string, interventie?: boolean) => void;
  exitCreateMode: () => void;
  executeCreateTask: (date: string, installerId: number) => Promise<void>;
  enterScheduleProjectMode: (projectId: number, projectName: string, faseId: number, faseName: string, address?: string) => void;
  exitScheduleProjectMode: () => void;
  executeScheduleProject: (date: string, installerId: number) => Promise<void>;
  setShowProjectSearch: (show: boolean) => void;
  setShowAllInstallers: (show: boolean) => void;
  setMultiWeek: (on: boolean) => void;
  setZoom: (zoom: number) => void;
  zoomIn: () => void;
  zoomOut: () => void;
  toggleHideCompleted: () => void;
  setProjectFilter: (filter: string) => void;
  setProjectIdFilter: (id: number | null) => void;
  deleteFeestdag: (taskId: number) => Promise<void>;
  moveTask: (taskId: number, sourceDate: string, sourceInstallerId: number, targetDate: string, targetInstallerId: number) => Promise<void>;
  toggleInkoopStatus: () => void;
  fetchInkoopStatus: () => Promise<void>;
  hydrateInstallerRowOrder: () => Promise<void>;
  setInstallerRowOrder: (installerIds: number[]) => void;
  persistInstallerRowOrder: (installerIds: number[]) => Promise<void>;
  resetInstallerRowOrder: () => Promise<void>;
  updateTaskInstallers: (taskId: number, installerIds: number[]) => void;

  lastClickedFaseId: number | null;

  cellContextMenu: { dateKey: string; installerId: number; x: number; y: number } | null;
  openCellContextMenu: (dateKey: string, installerId: number, x: number, y: number) => void;
  closeCellContextMenu: () => void;

  installerEditor: {
    taskId: number;
    originalIds: number[];
    selectedIds: number[];
    pos: { top?: number; bottom?: number; left: number; maxHeight: number };
    projectName: string;
  } | null;
  openInstallerEditor: (taskId: number, installerIds: number[], pos: { top?: number; bottom?: number; left: number; maxHeight: number }, projectName: string) => void;
  toggleInstallerInEditor: (installerId: number) => void;
  closeInstallerEditor: () => void;
}

/** Bumped per fetchPlanning call; a response that is no longer the latest is dropped. */
let planningFetchSeq = 0;

export const usePlanningStore = create<PlanningState>((set, get) => ({
  tasks: [],
  userMap: new Map(),
  projectleiderMap: new Map(),
  employeeMap: new Map(),
  categoryMap: new Map(),
  projectNameMap: new Map(),
  werfMap: new Map(),
  soNameMap: new Map(),
  vorderingProgress: new Map(),
  eindeWerkenProjects: new Set(),
  gevorderdFases: new Set(),
  inkoopStatusMap: new Map(),
  showInkoopStatus: false,
  loading: false,
  error: null,
  clearError: () => set({ error: null }),
  warning: null,
  clearWarning: () => set({ warning: null }),
  lastRefreshed: null,
  weekOffset: 0,
  viewFilter: 'installers',
  // Hendrika's group is collapsed on first load; the real ID is injected
  // from the API response below, this set is re-seeded after each fetch.
  collapsedGroups: new Set(),
  createMode: null,
  scheduleProjectMode: null,
  scheduleTargetCell: null,
  showProjectSearch: false,
  showAllInstallers: true,
  multiWeek: false,
  zoom: loadZoom(),
  hideCompleted: false,
  projectFilter: '',
  projectIdFilter: null,
  easykitConfig: null,
  installerRowOrder: [],
  installerRowOrderHydrated: false,

  fetchPlanning: async (weekOffset?: number, opts?: { silent?: boolean }) => {
    const offset = weekOffset ?? get().weekOffset;
    if (!opts?.silent) set({ loading: true, error: null });
    else set({ error: null });
    const seq = ++planningFetchSeq;
    try {
      // Date range for the API query: one week, or the multi-week window
      const { from, to } = getBoardRange(offset, get().multiWeek);

      const result = await fetchPlanningTasks(from, to);
      // A newer fetch (week switch, multi-week toggle) started meanwhile — its
      // result owns the board, and its own loading flag.
      if (seq !== planningFetchSeq) return;

      const userMap = new Map<number, string>();
      const employeeMap = new Map<number, boolean>();
      const categoryMap = new Map<number, string>();
      for (const [id, val] of Object.entries(result.installerMap)) {
        if (typeof val === 'string') {
          userMap.set(Number(id), val);
          employeeMap.set(Number(id), true);
          categoryMap.set(Number(id), 'td');
        } else {
          userMap.set(Number(id), val.name);
          employeeMap.set(Number(id), val.isEmployee);
          categoryMap.set(Number(id), val.category || (val.isEmployee ? 'td' : 'onderaannemer'));
        }
      }

      const projectleiderMap = new Map<number, string>();
      for (const [id, name] of Object.entries(result.projectleiderMap || {})) {
        projectleiderMap.set(Number(id), name);
      }

      const projectNameMap = new Map<number, string>();
      for (const [id, name] of Object.entries(result.projectNameMap)) {
        projectNameMap.set(Number(id), name);
      }

      const werfMap = new Map<number, string>();
      for (const [id, label] of Object.entries(result.werfMap)) {
        werfMap.set(Number(id), label);
      }

      const soNameMap = new Map<number, string>();
      for (const [id, name] of Object.entries(result.soNameMap || {})) {
        soNameMap.set(Number(id), name);
      }

      const vorderingProgress = new Map<number, { percentage: number; status: string }>(
        Object.entries(result.vorderingProgress || {}).map(([k, v]) => [Number(k), v as { percentage: number; status: string }]),
      );

      // Propagate parent's x_studio_planningstage to child tasks
      const parentStageMap = new Map<number, string>();
      for (const t of result.tasks) {
        if (!t.parent_id && t.x_studio_planningstage) {
          parentStageMap.set(t.id, t.x_studio_planningstage as string);
        }
      }
      for (const t of result.tasks) {
        if (t.parent_id && !t.x_studio_planningstage) {
          const parentId = Array.isArray(t.parent_id) ? t.parent_id[0] : 0;
          const parentStage = parentStageMap.get(parentId);
          if (parentStage) {
            t.x_studio_planningstage = parentStage;
          }
        }
      }

      // Build badge propagation sets
      const eindeWerkenProjects = new Set<number>();
      const gevorderdFases = new Set<number>();
      for (const t of result.tasks) {
        if (t.x_studio_planningstage === 'Einde werken Volledig Project') {
          const projectId = t.project_id ? t.project_id[0] : 0;
          if (projectId) eindeWerkenProjects.add(projectId);
        }
        if (t.x_studio_planningstage === 'Mag worden gevorderd') {
          const parentId = t.parent_id ? t.parent_id[0] : 0;
          if (parentId) gevorderdFases.add(parentId);
        }
      }

      // Seed Hendrika's collapsed-by-default state from API-provided config,
      // but only on the very first fetch — once the user has toggled groups
      // in-session, respect their choice.
      const priorConfig = get().easykitConfig;
      let collapsedGroups = get().collapsedGroups;
      if (!priorConfig && result.easykitConfig) {
        collapsedGroups = new Set([result.easykitConfig.plId]);
      }

      set({
        tasks: result.tasks,
        userMap,
        projectleiderMap,
        employeeMap,
        categoryMap,
        projectNameMap,
        werfMap,
        soNameMap,
        vorderingProgress,
        eindeWerkenProjects,
        gevorderdFases,
        easykitConfig: result.easykitConfig ?? null,
        collapsedGroups,
        loading: false,
        lastRefreshed: new Date(),
      });

      // Refresh inkoop status if toggle is on
      if (get().showInkoopStatus) {
        get().fetchInkoopStatus();
      }
    } catch (err) {
      if (seq !== planningFetchSeq) return;
      const message = err instanceof Error ? err.message : 'Planning ophalen mislukt';
      set({ error: message, loading: false });
    }
  },

  hydrateInstallerRowOrder: async () => {
    try {
      const result = await fetchPlanningPreferences();
      set({
        installerRowOrder: Array.isArray(result.installerRowOrder) ? result.installerRowOrder : [],
        // Remember the user's last "Toon alle installateurs" choice; default ON when unset.
        showAllInstallers: typeof result.showAllInstallers === 'boolean' ? result.showAllInstallers : true,
        // PlanningView awaits this before its first fetch, so the range is right first time.
        multiWeek: result.multiWeek === true,
        installerRowOrderHydrated: true,
      });
    } catch {
      set({
        installerRowOrder: [],
        installerRowOrderHydrated: true,
      });
    }
  },

  setInstallerRowOrder: (installerIds) => {
    set({ installerRowOrder: installerIds, installerRowOrderHydrated: true });
  },

  persistInstallerRowOrder: async (installerIds) => {
    try {
      const result = await savePlanningPreferences({ installerRowOrder: installerIds });
      set({
        installerRowOrder: Array.isArray(result.installerRowOrder) ? result.installerRowOrder : installerIds,
        installerRowOrderHydrated: true,
      });
    } catch {
      set({ error: 'Volgorde opslaan mislukt' });
    }
  },

  resetInstallerRowOrder: async () => {
    set({ installerRowOrder: [], installerRowOrderHydrated: true });
    try {
      const result = await savePlanningPreferences({ installerRowOrder: [] });
      set({
        installerRowOrder: Array.isArray(result.installerRowOrder) ? result.installerRowOrder : [],
        installerRowOrderHydrated: true,
      });
    } catch {
      set({ error: 'Volgorde resetten mislukt' });
    }
  },

  nextWeek: () => {
    const next = get().weekOffset + 1;
    set({ weekOffset: next, cellContextMenu: null, scheduleTargetCell: null });
    get().fetchPlanning(next);
  },
  prevWeek: () => {
    const prev = get().weekOffset - 1;
    set({ weekOffset: prev, cellContextMenu: null, scheduleTargetCell: null });
    get().fetchPlanning(prev);
  },
  goToCurrentWeek: () => {
    set({ weekOffset: 0, cellContextMenu: null, scheduleTargetCell: null });
    get().fetchPlanning(0);
  },
  setWeekOffset: (offset) => set({ weekOffset: offset }),
  // "Per dag" is an overview: every flow that can write is closed on the way in —
  // create/schedule modes, the cell menu and the Inplannen project search (whose
  // fase picker can create a fase). An open installer editor is closed the normal
  // way, which saves the change the user already made in the editable view.
  setViewFilter: (viewFilter) => {
    if (viewFilter !== 'per_day') {
      set({ viewFilter });
      return;
    }
    get().closeInstallerEditor();
    set({
      viewFilter,
      createMode: null,
      scheduleProjectMode: null,
      scheduleTargetCell: null,
      cellContextMenu: null,
      showProjectSearch: false,
    });
  },
  toggleGroup: (groupId) =>
    set((state) => {
      const next = new Set(state.collapsedGroups);
      if (next.has(groupId)) {
        next.delete(groupId);
      } else {
        next.add(groupId);
      }
      return { collapsedGroups: next };
    }),

  enterCreateMode: (type, name, interventie) => {
    set({ createMode: { type, name, ...(interventie ? { interventie } : {}) }, scheduleProjectMode: null, showProjectSearch: false, cellContextMenu: null });
  },

  exitCreateMode: () => {
    set({ createMode: null });
  },

  executeCreateTask: async (date, installerId) => {
    const { createMode } = get();
    if (!createMode) return;

    if (createMode.type === 'vrije_taak') {
      set({ createMode: null });
    }

    let warning: string | undefined;
    try {
      if (createMode.type === 'feestdag') {
        // Single marker task — no installers needed, frontend shows purple column
        await createPlanningTask({ type: 'feestdag', date, installerIds: [], name: createMode.name });
      } else if (createMode.type === 'verlof') {
        await createPlanningTask({ type: 'verlof', date, installerIds: [installerId] });
      } else {
        const res = await createPlanningTask({
          type: 'vrije_taak', date, installerIds: [installerId], name: createMode.name,
          ...(createMode.interventie ? { interventie: true } : {}),
        });
        warning = res.warning;
      }
      // The project exists but its interventie tag did not stick: the user must tick
      // it by hand, so this stays until closed (no rollback).
      if (warning) set({ warning });
      await get().fetchPlanning();
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Taak aanmaken mislukt';
      set({ error: message });
    }
  },

  enterScheduleProjectMode: (projectId, projectName, faseId, faseName, address) => {
    const target = get().scheduleTargetCell;
    if (target) {
      set({ createMode: null, showProjectSearch: false, cellContextMenu: null, loading: true });
      scheduleFase({ faseId, projectId, installerIds: [target.installerId], date: target.dateKey })
        .then(() => {
          set({ scheduleProjectMode: null, scheduleTargetCell: null });
          return get().fetchPlanning();
        })
        .catch((err) => {
          const message = err instanceof Error ? err.message : 'Inplannen mislukt';
          set({ error: message, loading: false });
        });
      return;
    }
    set({ scheduleProjectMode: { projectId, projectName, faseId, faseName, address }, createMode: null, showProjectSearch: false, cellContextMenu: null });
  },

  exitScheduleProjectMode: () => {
    set({ scheduleProjectMode: null, scheduleTargetCell: null });
  },

  executeScheduleProject: async (date, installerId) => {
    const { scheduleProjectMode } = get();
    if (!scheduleProjectMode) return;

    try {
      await scheduleFase({
        faseId: scheduleProjectMode.faseId,
        projectId: scheduleProjectMode.projectId,
        installerIds: [installerId],
        date,
      });
      set({ scheduleProjectMode: null, scheduleTargetCell: null });
      await get().fetchPlanning();
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Inplannen mislukt';
      set({ error: message });
    }
  },

  setShowProjectSearch: (show) => {
    set({ showProjectSearch: show, createMode: null, scheduleProjectMode: null, cellContextMenu: null });
  },

  setShowAllInstallers: (show) => {
    set({ showAllInstallers: show });
    // Best-effort persist the choice. Read-only planning viewers (planning:read
    // without planning:write) get a 403 here — swallow it so the toggle stays
    // session-local for them, mirroring persistInstallerRowOrder's resilience.
    void savePlanningPreferences({ showAllInstallers: show }).catch(() => {});
  },

  setMultiWeek: (on) => {
    set({ multiWeek: on, cellContextMenu: null, scheduleTargetCell: null });
    void get().fetchPlanning();
    // Best-effort persist, same as setShowAllInstallers (read-only viewers get 403).
    void savePlanningPreferences({ multiWeek: on }).catch(() => {});
  },

  setZoom: (zoom) => {
    set({ zoom });
    saveZoom(zoom);
  },
  zoomIn: () => get().setZoom(stepZoom(get().zoom, 1)),
  zoomOut: () => get().setZoom(stepZoom(get().zoom, -1)),

  toggleHideCompleted: () => set((state) => ({ hideCompleted: !state.hideCompleted })),

  setProjectFilter: (filter) => set({ projectFilter: filter }),
  setProjectIdFilter: (id) => set({ projectIdFilter: id }),

  toggleInkoopStatus: () => {
    const next = !get().showInkoopStatus;
    set({ showInkoopStatus: next });
    if (next && get().inkoopStatusMap.size === 0) {
      get().fetchInkoopStatus();
    }
  },

  fetchInkoopStatus: async () => {
    // Collect unique project IDs from currently loaded tasks
    const projectIds = new Set<number>();
    for (const t of get().tasks) {
      const pid = t.project_id ? (Array.isArray(t.project_id) ? t.project_id[0] : 0) : 0;
      if (pid) projectIds.add(pid);
    }
    if (projectIds.size === 0) {
      set({ inkoopStatusMap: new Map() });
      return;
    }
    // Clear stale entries for projects no longer visible (e.g. after week switch)
    const currentMap = get().inkoopStatusMap;
    const filtered = new Map<number, InkoopStatus>();
    for (const [k, v] of currentMap) {
      if (projectIds.has(k)) filtered.set(k, v);
    }
    set({ inkoopStatusMap: filtered });
    try {
      const result = await fetchInkoopStatusBulk([...projectIds]);
      const map = new Map<number, InkoopStatus>();
      for (const [k, v] of Object.entries(result)) {
        map.set(Number(k), v);
      }
      set({ inkoopStatusMap: map });
    } catch (err) {
      // Informational feature — log but don't disrupt UX
      console.warn('Inkoop status fetch failed', err);
    }
  },

  deleteFeestdag: async (taskId) => {
    set({ tasks: get().tasks.filter((t) => t.id !== taskId) });
    try {
      await deletePlanningTask(taskId);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Verwijderen mislukt';
      set({ error: message });
      await get().fetchPlanning();
    }
  },

  moveTask: async (taskId, sourceDate, sourceInstallerId, targetDate, targetInstallerId) => {
    const task = get().tasks.find((t) => t.id === taskId);
    if (!task) return;

    // Block drop if target date already has a sibling from the same fase
    if (targetDate !== sourceDate && task.parent_id) {
      const parentId = task.parent_id[0];
      const siblingOnTarget = get().tasks.find(
        (t) => t.id !== taskId
          && t.parent_id
          && t.parent_id[0] === parentId
          && t.date_deadline
          && t.date_deadline.slice(0, 10) === targetDate,
      );
      if (siblingOnTarget) {
        const hasInstallers = Array.isArray(siblingOnTarget.x_studio_installateur) && siblingOnTarget.x_studio_installateur.length > 0;
        if (hasInstallers) {
          set({ error: 'Taak van dit project al ingepland op deze dag' });
        } else {
          set({ error: 'Taak al op deze dag — kijk bij niet toegewezen installateurs' });
        }
        return;
      }
    }

    const updates: Record<string, unknown> = {};

    // Date change
    if (targetDate !== sourceDate) {
      updates.date_deadline = targetDate;
    }

    // Installer change logic:
    // - Target NOT on task → replace source with target
    // - Target already on task → remove source (drag within same task's rows)
    if (targetInstallerId !== sourceInstallerId) {
      const currentInstallers = [...(task.x_studio_installateur || [])];
      const sourceIdx = currentInstallers.indexOf(sourceInstallerId);

      if (!currentInstallers.includes(targetInstallerId)) {
        // Target is a NEW installer — replace source with target
        if (sourceIdx !== -1) {
          currentInstallers[sourceIdx] = targetInstallerId;
        } else {
          currentInstallers.push(targetInstallerId);
        }
        updates.x_studio_installateur = currentInstallers;
      } else if (sourceIdx !== -1) {
        // Target already on task → remove source installer
        currentInstallers.splice(sourceIdx, 1);
        updates.x_studio_installateur = currentInstallers;
      }
    }

    if (Object.keys(updates).length === 0) return;

    set({
      tasks: get().tasks.map((t) =>
        t.id === taskId ? { ...t, ...updates } : t,
      ),
    });

    try {
      await updatePlanningTask(taskId, updates);
    } catch (err) {
      set({ error: err instanceof Error ? err.message : 'Verplaatsen mislukt' });
      await get().fetchPlanning();
    }
  },

  updateTaskInstallers: (taskId, installerIds) => {
    set({
      tasks: get().tasks.map((t) =>
        t.id === taskId ? { ...t, x_studio_installateur: installerIds } : t,
      ),
    });
  },

  lastClickedFaseId: null,

  cellContextMenu: null,

  openCellContextMenu: (dateKey, installerId, x, y) => {
    set({ cellContextMenu: { dateKey, installerId, x, y } });
  },

  closeCellContextMenu: () => {
    set({ cellContextMenu: null });
  },

  installerEditor: null,

  openInstallerEditor: (taskId, installerIds, pos, projectName) => {
    set({
      installerEditor: {
        taskId,
        originalIds: [...installerIds],
        selectedIds: [...installerIds],
        pos,
        projectName,
      },
    });
  },

  toggleInstallerInEditor: (installerId) => {
    const editor = get().installerEditor;
    if (!editor) return;
    const newIds = editor.selectedIds.includes(installerId)
      ? editor.selectedIds.filter((id) => id !== installerId)
      : [...editor.selectedIds, installerId];
    set({
      installerEditor: { ...editor, selectedIds: newIds },
      tasks: get().tasks.map((t) =>
        t.id === editor.taskId ? { ...t, x_studio_installateur: newIds } : t,
      ),
    });
  },

  closeInstallerEditor: () => {
    const editor = get().installerEditor;
    if (!editor) return;
    set({ installerEditor: null });
    const changed =
      editor.selectedIds.length !== editor.originalIds.length ||
      editor.selectedIds.some((id) => !editor.originalIds.includes(id));
    if (changed) {
      updatePlanningTask(editor.taskId, { x_studio_installateur: editor.selectedIds }).catch(() => {
        set({
          error: 'Installateurs opslaan mislukt',
          tasks: get().tasks.map((t) =>
            t.id === editor.taskId ? { ...t, x_studio_installateur: editor.originalIds } : t,
          ),
        });
      });
    }
  },
}));
